/**
 * Change password handler
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import BaseHandler from './BaseHandler';
import DOMUtils from '../utils/DOMUtils';
import FormValidator from '../utils/FormValidator';
import { FORM_SELECTORS, FIELD_SELECTORS, MESSAGE_SELECTORS } from '../constants';

class ChangePasswordHandler extends BaseHandler {
  constructor(form, api, stepNavigator, messages) {
    super(form, api, stepNavigator, messages);
    this.validator = new FormValidator(messages);
    this.bindPasswordValidation();
  }

  /**
   * Bind real-time password validation
   */
  bindPasswordValidation() {
    const passwordInput = this.form.querySelector(FIELD_SELECTORS.newPassword);
    const passwordConfirmInput = this.form.querySelector(FIELD_SELECTORS.newPasswordConfirm);

    if (!passwordInput || !passwordConfirmInput) {
      return;
    }

    const validateConfirm = () => {
      const password = passwordInput.value;
      const passwordConfirm = passwordConfirmInput.value;

      if (password && !passwordConfirm) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.validator.createError('error_field_required'),
          false,
          false,
        );
        return;
      }

      if (!password && passwordConfirm) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.messages.getMessage('error_password_match'),
          false,
          false,
        );
        return;
      }

      if (
        password
        && passwordConfirm
        && !FormValidator.validatePasswordsMatch(password, passwordConfirm)
      ) {
        DOMUtils.showError(
          passwordConfirmInput,
          this.messages.getMessage('error_password_match'),
          false,
          false,
        );
        return;
      }

      DOMUtils.clearError(passwordConfirmInput);
    };

    const validatePassword = () => {
      const password = passwordInput.value;

      const passwordError = this.validator.validatePassword(password);
      if (passwordError) {
        DOMUtils.showError(passwordInput, passwordError, false, false);
      } else {
        DOMUtils.clearError(passwordInput);
      }

      validateConfirm();
    };

    passwordInput.addEventListener('input', validatePassword);
    passwordConfirmInput.addEventListener('input', validateConfirm);
  }

  /**
   * Validate and show password change form
   */
  async validateAndShowChangePasswordForm(key, login) {
    try {
      const { response, data } = await this.api.validateResetKey(key, login);

      if (response.ok && data.valid) {
        // Set values in hidden fields
        DOMUtils.setFieldValue(FIELD_SELECTORS.resetKey, key, this.form);
        DOMUtils.setFieldValue(FIELD_SELECTORS.resetLogin, login, this.form);

        // Fill email field from response
        if (data.user && data.user.email) {
          DOMUtils.setFieldValue(FIELD_SELECTORS.resetEmailDisplay, data.user.email, this.form);
        }

        // Show password change form
        this.stepNavigator.goToStep('change-password');

        // Track Enter New Password page load
        pushFunnelEvent({
          section: 'Forgot Password',
          step: 3,
          label: 'Forgot Password - Enter New Password',
          method: 'Email',
        });

        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (data.code === 'invalid_key') {
        // Check if error is invalid_key - if so, go to reset-password step
        this.stepNavigator.goToStep('reset-password');
        this.initResetPasswordEmail();

        const messageElement = this.form.querySelector(MESSAGE_SELECTORS.resetMessage);
        const message = this.getErrorMessage(data, 'error_invalid_key');
        DOMUtils.showMessage(messageElement, message, 'error');
      } else {
        // Show password change form first to make message element available
        this.stepNavigator.goToStep('change-password');

        const messageElement = this.form.querySelector(MESSAGE_SELECTORS.changePasswordMessage);
        const message = this.getErrorMessage(data, 'error_invalid_key');
        DOMUtils.showMessage(messageElement, message, 'error');
      }
    } catch (error) {
      // Show password change form first to make message element available
      this.stepNavigator.goToStep('change-password');

      const messageElement = this.form.querySelector(MESSAGE_SELECTORS.changePasswordMessage);
      const message = this.messages.getMessage('error_connection');
      DOMUtils.showMessage(messageElement, message, 'error');
    }
  }

  /**
   * Handle password change
   */
  async handleChangePassword() {
    const passwordInput = this.form.querySelector(FIELD_SELECTORS.newPassword);
    const passwordConfirmInput = this.form.querySelector(FIELD_SELECTORS.newPasswordConfirm);
    const key = DOMUtils.getFieldValue(FIELD_SELECTORS.resetKey, this.form);
    const login = DOMUtils.getFieldValue(FIELD_SELECTORS.resetLogin, this.form);
    const password = passwordInput.value;
    const passwordConfirm = passwordConfirmInput.value;

    // Form validation
    const errors = this.validator.validateChangePasswordForm(password, passwordConfirm);
    if (errors) {
      if (errors.password) {
        DOMUtils.showError(passwordInput, errors.password);
      }
      if (errors.password_confirm) {
        DOMUtils.showError(
          passwordConfirmInput,
          errors.password_confirm,
        );
      }

      // Track validation error
      const errorMessage = errors.password || errors.password_confirm;
      pushFunnelEvent({
        section: 'Forgot Password',
        step: 4,
        label: 'Forgot Password - Error',
        method: 'Email',
        errorMessage,
      });

      return;
    }

    // Track Next button click
    pushFunnelEvent({
      section: 'Forgot Password',
      step: 4,
      label: 'Forgot Password - Next',
      method: 'Email',
    });

    await this.handleApiCall(
      FORM_SELECTORS.changePassword,
      () => this.api.changePassword(key, login, password, passwordConfirm),
      {
        messageElement: this.form.querySelector(MESSAGE_SELECTORS.changePasswordMessage),
        successMessageKey: 'message_password_changed',
        errorMessageKey: 'error_password_change',
        successCallback: (data) => {
          // Track success
          pushFunnelEvent({
            section: 'Forgot Password',
            step: 5,
            label: 'Forgot Password - Success',
            method: 'Email',
          });

          window.location.href = data.redirect || window.location.href;
        },
        errorCallback: (data) => {
          const errorMessage = this.getErrorMessage(data, 'error_password_change');

          // Track API error
          pushFunnelEvent({
            section: 'Forgot Password',
            step: 4,
            label: 'Forgot Password - Error',
            method: 'Email',
            errorMessage,
          });
        },
      },
    );
  }
}

export default ChangePasswordHandler;
